/* eslint-disable react/jsx-props-no-spreading */
import { useIsVisible } from '@folklore/hooks';
import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import { useCallback } from 'react';

import { CommentsQuery, useBlockComment, useCommentsList } from '../../hooks/useComments';

import RoundedButton from '../buttons/RoundedButton';
import CommentCard from '../cards/CommentCard';
import CommentForm from '../forms/CommentForm';
import FormTitle from '../typography/FormTitle';

import styles from '../../styles/lists/comments-list.module.css';

interface CommentsListProps {
    commentableKey: string | null;
    query?: CommentsQuery | null;
    placeholder?: boolean;
    loading?: Loading;
    disabled?: boolean;
    className?: string | null;
}

function CommentsList({
    commentableKey,
    query,
    loading = 'lazy',
    placeholder = false,
    disabled = false,
    className,
}: CommentsListProps) {
    const isLazyLoading = loading === 'lazy';
    const { ref, visible: isVisible = false } = useIsVisible({
        persist: true,
        // disabled: true,
        disabled: !isLazyLoading || disabled,
    });

    const shouldLoad = (!isLazyLoading || isVisible) && !disabled;
    const {
        items,
        total = 0,
        isFetching = false,
        endIsReached = false,
        isLoading = false,
        fetchNextPage,
        addComment,
    } = useCommentsList(commentableKey, query, {
        enabled: shouldLoad,
    });

    const tracking = useTracking();

    const onClickLoadMore = useCallback(() => {
        fetchNextPage();
        tracking.trackEvent('Comments', 'load_more');
    }, [fetchNextPage, tracking]);

    const onFormComplete = useCallback(
        (newComment) => {
            addComment(newComment);
            tracking.trackEvent('Comments', 'create', null, null, {
                comment: newComment,
            });
        },
        [addComment, tracking],
    );

    const onCommentUpdated = useCallback(
        (newComment) => {
            addComment(newComment);
        },
        [addComment],
    );

    return (
        <div className={classNames([styles.container, className])} ref={ref}>
            <FormTitle className={styles.title}>
                Commentaires{total > 0 ? ` (${total})` : ''}
            </FormTitle>
            <div className={styles.write}>
                <CommentForm
                    commentableKey={commentableKey}
                    onComplete={onFormComplete}
                    className={styles.form}
                />
            </div>
            {items.length > 0 || isLoading ? (
                <div className={styles.comments}>
                    {(items || []).map((item) => (
                        <CommentCard
                            key={`comment-${item.id}`}
                            {...item}
                            onUpdated={onCommentUpdated}
                            commentableKey={commentableKey}
                            query={query}
                            className={styles.comment}
                        />
                    ))}
                    {!endIsReached && !isLoading ? (
                        <RoundedButton
                            onClick={onClickLoadMore}
                            disabled={isFetching}
                            className={styles.loadMore}
                        >
                            Charger plus de commentaires
                        </RoundedButton>
                    ) : null}
                </div>
            ) : (
                <div className={styles.noComments}>
                    Aucun commentaire pour le moment.
                    <br />
                    Soyez le premier à commenter!
                </div>
            )}
        </div>
    );
}

export default CommentsList;
